import { useState, useEffect } from "react";
import { Receipt, Plus, RefreshCw, TrendingUp, DollarSign, BarChart3, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import axios from "axios";

const ACCOUNT_TYPES = ["ASSET", "LIABILITY", "EQUITY", "REVENUE", "EXPENSE"];

export default function ERPFinance() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("chart-of-accounts");
  const [accounts, setAccounts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [arAging, setArAging] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [cashForecast, setCashForecast] = useState(null);
  const [loading, setLoading] = useState({});
  const [showNewAccount, setShowNewAccount] = useState(false);
  const [showNewJE, setShowNewJE] = useState(false);
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [showNewVendor, setShowNewVendor] = useState(false);

  const [newAccount, setNewAccount] = useState({ account_code: "", account_name: "", account_type: "EXPENSE" });
  const [newJE, setNewJE] = useState({ description: "", reference: "", lines: [{ account_id: "", debit_amount: 0, credit_amount: 0 }] });
  const [newCustomer, setNewCustomer] = useState({ customer_code: "", company_name: "", tax_id: "", payment_terms: "NET30" });
  const [newVendor, setNewVendor] = useState({ vendor_code: "", company_name: "", tax_id: "", payment_terms: "NET30" });

  const fetchAccounts = async () => {
    setLoading(prev => ({ ...prev, accounts: true }));
    try {
      const res = await axios.get("/api/erp/chart-of-accounts");
      setAccounts(Array.isArray(res.data) ? res.data : []);
    } catch (err) { toast({ title: "Failed to load accounts", variant: "destructive" }); }
    finally { setLoading(prev => ({ ...prev, accounts: false })); }
  };

  const fetchCustomers = async () => {
    try {
      const res = await axios.get("/api/erp/customers");
      setCustomers(res.data.customers || []);
    } catch (err) { /* ignore */ }
  };

  const fetchVendors = async () => {
    try {
      const res = await axios.get("/api/erp/vendors");
      setVendors(res.data.vendors || []);
    } catch (err) { /* ignore */ }
  };

  const fetchArAging = async () => {
    setLoading(prev => ({ ...prev, ar: true }));
    try {
      const res = await axios.get("/api/erp/ar-aging");
      setArAging(res.data.aging || []);
    } catch (err) { toast({ title: "Failed to load AR aging", variant: "destructive" }); }
    finally { setLoading(prev => ({ ...prev, ar: false })); }
  };

  const fetchJournalEntries = async () => {
    setLoading(prev => ({ ...prev, je: true }));
    try {
      const res = await axios.get("/api/erp/journal-entries");
      setJournalEntries(res.data || []);
    } catch (err) { toast({ title: "Failed to load journal entries", variant: "destructive" }); }
    finally { setLoading(prev => ({ ...prev, je: false })); }
  };

  const fetchCashForecast = async (days = 30) => {
    setLoading(prev => ({ ...prev, forecast: true }));
    try {
      const res = await axios.post("/api/erp/cash-forecast", { days });
      setCashForecast(res.data);
    } catch (err) { toast({ title: "Failed to load cash forecast", variant: "destructive" }); }
    finally { setLoading(prev => ({ ...prev, forecast: false })); }
  };

  useEffect(() => {
    fetchAccounts();
    fetchCustomers();
    fetchVendors();
  }, []);

  const handleCreateAccount = async () => {
    try {
      await axios.post("/api/erp/chart-of-accounts", newAccount);
      toast({ title: "Account created" });
      setShowNewAccount(false);
      fetchAccounts();
    } catch (err) { toast({ title: "Failed to create account", variant: "destructive" }); }
  };

  const handleCreateJE = async () => {
    try {
      await axios.post("/api/erp/journal-entries", newJE);
      toast({ title: "Journal entry created" });
      setShowNewJE(false);
      fetchJournalEntries();
    } catch (err) { toast({ title: err.response?.data?.detail || "Failed", variant: "destructive" }); }
  };

  const handlePostJE = async (id) => {
    try {
      await axios.post(`/api/erp/journal-entries/${id}/post`);
      toast({ title: "Journal entry posted" });
      fetchJournalEntries();
    } catch (err) { toast({ title: err.response?.data?.detail || "Failed to post", variant: "destructive" }); }
  };

  const handleCreateCustomer = async () => {
    try {
      await axios.post("/api/erp/customers", newCustomer);
      toast({ title: "Customer created" });
      setShowNewCustomer(false);
      fetchCustomers();
    } catch (err) { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleCreateVendor = async () => {
    try {
      await axios.post("/api/erp/vendors", newVendor);
      toast({ title: "Vendor created" });
      setShowNewVendor(false);
      fetchVendors();
    } catch (err) { toast({ title: "Failed", variant: "destructive" }); }
  };

  const addJELine = () => {
    setNewJE(prev => ({ ...prev, lines: [...prev.lines, { account_id: "", debit_amount: 0, credit_amount: 0 }] }));
  };

  const updateJELine = (index, field, value) => {
    const lines = [...newJE.lines];
    lines[index][field] = value;
    setNewJE(prev => ({ ...prev, lines }));
  };

  const totalDebit = newJE.lines.reduce((s, l) => s + Number(l.debit_amount || 0), 0);
  const totalCredit = newJE.lines.reduce((s, l) => s + Number(l.credit_amount || 0), 0);

  const renderAccounts = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Chart of Accounts</h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchAccounts}>
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Dialog open={showNewAccount} onOpenChange={setShowNewAccount}>
            <DialogTrigger asChild>
              <Button size="sm"><Plus className="h-4 w-4 mr-1" /> New Account</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Account</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Account Code</Label><Input value={newAccount.account_code} onChange={e => setNewAccount(p => ({ ...p, account_code: e.target.value }))} /></div>
                <div><Label>Account Name</Label><Input value={newAccount.account_name} onChange={e => setNewAccount(p => ({ ...p, account_name: e.target.value }))} /></div>
                <div><Label>Type</Label><Select value={newAccount.account_type} onValueChange={v => setNewAccount(p => ({ ...p, account_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{ACCOUNT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></div>
                <Button onClick={handleCreateAccount} className="w-full">Create</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Type</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {accounts.map(acct => (
              <TableRow key={acct.id}>
                <TableCell className="font-mono">{acct.account_code}</TableCell>
                <TableCell>{acct.account_name}</TableCell>
                <TableCell><Badge variant="outline">{acct.account_type}</Badge></TableCell>
              </TableRow>
            ))}
            {accounts.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-8">No accounts found</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  const renderJournalEntries = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Journal Entries</h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchJournalEntries}>
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Dialog open={showNewJE} onOpenChange={setShowNewJE}>
            <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-1" /> New Entry</Button></DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>New Journal Entry</DialogTitle></DialogHeader>
              <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                <div><Label>Description</Label><Textarea value={newJE.description} onChange={e => setNewJE(p => ({ ...p, description: e.target.value }))} /></div>
                <div><Label>Reference</Label><Input value={newJE.reference} onChange={e => setNewJE(p => ({ ...p, reference: e.target.value }))} /></div>
                <Separator />
                <div className="flex justify-between items-center">
                  <span className="font-medium">Lines</span>
                  <Button variant="outline" size="sm" onClick={addJELine}>Add Line</Button>
                </div>
                {newJE.lines.map((line, i) => (
                  <div key={i} className="flex gap-2 items-start">
                    <div className="flex-1">
                      <Select value={line.account_id} onValueChange={v => updateJELine(i, "account_id", v)}>
                        <SelectTrigger><SelectValue placeholder="Account" /></SelectTrigger>
                        <SelectContent>{accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.account_code} - {a.account_name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="w-28"><Input type="number" placeholder="Debit" value={line.debit_amount || ""} onChange={e => updateJELine(i, "debit_amount", parseFloat(e.target.value) || 0)} /></div>
                    <div className="w-28"><Input type="number" placeholder="Credit" value={line.credit_amount || ""} onChange={e => updateJELine(i, "credit_amount", parseFloat(e.target.value) || 0)} /></div>
                  </div>
                ))}
                <div className="flex justify-between text-sm font-mono pt-2 border-t">
                  <span>Total Debit: ${totalDebit.toFixed(2)}</span>
                  <span>Total Credit: ${totalCredit.toFixed(2)}</span>
                  <span className={Math.abs(totalDebit - totalCredit) < 0.01 ? "text-green-600" : "text-red-600"}>
                    {Math.abs(totalDebit - totalCredit) < 0.01 ? "Balanced" : `Diff: ${(totalDebit - totalCredit).toFixed(2)}`}
                  </span>
                </div>
                <Button onClick={handleCreateJE} className="w-full" disabled={Math.abs(totalDebit - totalCredit) >= 0.01}>Create Entry</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Entry #</TableHead><TableHead>Description</TableHead><TableHead>Debit</TableHead><TableHead>Credit</TableHead><TableHead>Status</TableHead><TableHead>Action</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {journalEntries.map(je => (
              <TableRow key={je.id}>
                <TableCell className="font-mono text-xs">{je.entry_number}</TableCell>
                <TableCell>{je.description}</TableCell>
                <TableCell>${je.total_debit?.toFixed(2)}</TableCell>
                <TableCell>${je.total_credit?.toFixed(2)}</TableCell>
                <TableCell><Badge variant={je.status === "POSTED" ? "default" : "secondary"}>{je.status}</Badge></TableCell>
                <TableCell>{je.status === "DRAFT" && <Button size="sm" variant="outline" onClick={() => handlePostJE(je.id)}>Post</Button>}</TableCell>
              </TableRow>
            ))}
            {journalEntries.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No entries</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  const renderArAging = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">AR Aging</h2>
        <Button variant="outline" size="sm" onClick={fetchArAging}><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Invoice</TableHead><TableHead>Customer</TableHead><TableHead>Amount</TableHead><TableHead>Due Date</TableHead><TableHead>Days Overdue</TableHead><TableHead>Bucket</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {arAging.map((inv, i) => (
              <TableRow key={i}>
                <TableCell className="font-mono text-xs">{inv.invoice_number}</TableCell>
                <TableCell>{inv.customer_id?.slice(0, 12)}...</TableCell>
                <TableCell>${inv.amount?.toFixed(2)}</TableCell>
                <TableCell>{inv.due_date}</TableCell>
                <TableCell>{inv.days_overdue}</TableCell>
                <TableCell><Badge variant={inv.bucket === "CURRENT" ? "secondary" : inv.bucket === "1-30" ? "warning" : "destructive"}>{inv.bucket}</Badge></TableCell>
              </TableRow>
            ))}
            {arAging.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No aging data</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  const renderCashForecast = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Cash Flow Forecast</h2>
        <Button variant="outline" size="sm" onClick={() => fetchCashForecast()}><BarChart3 className="h-4 w-4 mr-1" /> Generate</Button>
      </div>
      {cashForecast ? (
        <div className="space-y-4">
          {cashForecast.ai_summary && <Card className="bg-blue-50 border-blue-200"><CardContent className="p-4"><p className="text-sm text-blue-800">{cashForecast.ai_summary}</p></CardContent></Card>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-sm">Receivables Due</CardTitle></CardHeader>
              <CardContent>
                {cashForecast.receivables?.length > 0 ? cashForecast.receivables.slice(0, 10).map((r, i) => (
                  <div key={i} className="flex justify-between text-sm"><span>{r.date}</span><span className="font-mono text-green-600">+${r.amount.toFixed(2)}</span></div>
                )) : <p className="text-sm text-muted-foreground">No receivables due</p>}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-sm">Payables Due</CardTitle></CardHeader>
              <CardContent>
                {cashForecast.payables?.length > 0 ? cashForecast.payables.slice(0, 10).map((p, i) => (
                  <div key={i} className="flex justify-between text-sm"><span>{p.date}</span><span className="font-mono text-red-600">-${p.amount.toFixed(2)}</span></div>
                )) : <p className="text-sm text-muted-foreground">No payables due</p>}
              </CardContent>
            </Card>
          </div>
        </div>
      ) : (
        <Card><CardContent className="p-8 text-center text-muted-foreground">Click "Generate" to run the AI-powered cash flow forecast</CardContent></Card>
      )}
    </div>
  );

  const renderCustomers = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Customers</h2>
        <Dialog open={showNewCustomer} onOpenChange={setShowNewCustomer}>
          <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-1" /> New Customer</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Customer</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Code</Label><Input value={newCustomer.customer_code} onChange={e => setNewCustomer(p => ({ ...p, customer_code: e.target.value }))} /></div>
              <div><Label>Company Name</Label><Input value={newCustomer.company_name} onChange={e => setNewCustomer(p => ({ ...p, company_name: e.target.value }))} /></div>
              <div><Label>Tax ID</Label><Input value={newCustomer.tax_id} onChange={e => setNewCustomer(p => ({ ...p, tax_id: e.target.value }))} /></div>
              <div><Label>Payment Terms</Label><Select value={newCustomer.payment_terms} onValueChange={v => setNewCustomer(p => ({ ...p, payment_terms: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="NET15">NET15</SelectItem><SelectItem value="NET30">NET30</SelectItem><SelectItem value="NET60">NET60</SelectItem><SelectItem value="CASH">Cash on Delivery</SelectItem></SelectContent>
              </Select></div>
              <Button onClick={handleCreateCustomer} className="w-full">Create</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Code</TableHead><TableHead>Company</TableHead><TableHead>Tax ID</TableHead><TableHead>Terms</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {customers.map(c => <TableRow key={c.id}><TableCell className="font-mono">{c.customer_code}</TableCell><TableCell>{c.company_name}</TableCell><TableCell>{c.tax_id || "-"}</TableCell><TableCell>{c.payment_terms}</TableCell></TableRow>)}
            {customers.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No customers</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  const renderVendors = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Vendors</h2>
        <Dialog open={showNewVendor} onOpenChange={setShowNewVendor}>
          <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-1" /> New Vendor</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Vendor</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Code</Label><Input value={newVendor.vendor_code} onChange={e => setNewVendor(p => ({ ...p, vendor_code: e.target.value }))} /></div>
              <div><Label>Company Name</Label><Input value={newVendor.company_name} onChange={e => setNewVendor(p => ({ ...p, company_name: e.target.value }))} /></div>
              <div><Label>Tax ID</Label><Input value={newVendor.tax_id} onChange={e => setNewVendor(p => ({ ...p, tax_id: e.target.value }))} /></div>
              <div><Label>Payment Terms</Label><Select value={newVendor.payment_terms} onValueChange={v => setNewVendor(p => ({ ...p, payment_terms: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="NET15">NET15</SelectItem><SelectItem value="NET30">NET30</SelectItem><SelectItem value="NET60">NET60</SelectItem></SelectContent>
              </Select></div>
              <Button onClick={handleCreateVendor} className="w-full">Create</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Code</TableHead><TableHead>Company</TableHead><TableHead>Tax ID</TableHead><TableHead>Terms</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {vendors.map(v => <TableRow key={v.id}><TableCell className="font-mono">{v.vendor_code}</TableCell><TableCell>{v.company_name}</TableCell><TableCell>{v.tax_id || "-"}</TableCell><TableCell>{v.payment_terms}</TableCell></TableRow>)}
            {vendors.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No vendors</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Finance & Controlling</h1>
        <p className="text-muted-foreground">General ledger, accounts payable/receivable, cash flow</p>
      </div>
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="chart-of-accounts">Chart of Accounts</TabsTrigger>
          <TabsTrigger value="journal-entries">Journal Entries</TabsTrigger>
          <TabsTrigger value="ar-aging">AR Aging</TabsTrigger>
          <TabsTrigger value="cash-forecast">Cash Forecast</TabsTrigger>
          <TabsTrigger value="customers">Customers</TabsTrigger>
          <TabsTrigger value="vendors">Vendors</TabsTrigger>
        </TabsList>
        <TabsContent value="chart-of-accounts" className="mt-4">{renderAccounts()}</TabsContent>
        <TabsContent value="journal-entries" className="mt-4">{renderJournalEntries()}</TabsContent>
        <TabsContent value="ar-aging" className="mt-4">{renderArAging()}</TabsContent>
        <TabsContent value="cash-forecast" className="mt-4">{renderCashForecast()}</TabsContent>
        <TabsContent value="customers" className="mt-4">{renderCustomers()}</TabsContent>
        <TabsContent value="vendors" className="mt-4">{renderVendors()}</TabsContent>
      </Tabs>
    </div>
  );
}
